import React, { useState } from 'react';
import { Settings, Save, User } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { COMMON_TIMEZONES } from '../utils/dateUtils';

const inputCls = 'w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-900';

// ── My Profile Tab ─────────────────────────────────────────────────────────────
function ProfileTab() {
  const { user, setUser } = useAuth();
  const [timezone, setTimezone] = useState(user?.timezone || 'UTC');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/api/auth/profile', { timezone });
      setUser(u => ({ ...u, timezone: res.data.timezone }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg" data-testid="profile-tab">
      <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-5">
        <div>
          <h2 className="font-semibold text-slate-800 mb-0.5" style={{ fontFamily: 'Chivo, sans-serif' }}>My Profile</h2>
          <p className="text-xs text-slate-400">Your personal settings. Changes apply to your session only.</p>
        </div>

        {/* Read-only info */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-xs font-medium text-slate-500 mb-0.5">Name</p>
            <p className="text-slate-800">{user?.full_name || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 mb-0.5">Email</p>
            <p className="text-slate-800">{user?.email || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500 mb-0.5">Role</p>
            <p className="text-slate-800 capitalize">{user?.role || '—'}</p>
          </div>
        </div>

        <hr className="border-slate-100" />

        {/* Timezone */}
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1.5">Display Timezone</label>
          <select
            value={timezone}
            onChange={e => setTimezone(e.target.value)}
            className={inputCls}
            data-testid="timezone-select"
          >
            {COMMON_TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label} ({tz.value})</option>
            ))}
          </select>
          <p className="text-xs text-slate-400 mt-1">
            All date and time values in the app will display in this timezone.
          </p>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-900 text-white text-sm font-medium rounded-md hover:bg-indigo-800 disabled:opacity-50"
            data-testid="save-profile-btn">
            <Save size={13} /> {saving ? 'Saving…' : 'Save'}
          </button>
          {saved && <span className="text-xs text-green-600 font-medium">Saved!</span>}
        </div>
      </div>
    </div>
  );
}

export default function SystemAdmin() {
  return (
    <div className="p-6 fade-in" data-testid="system-admin-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Settings size={18} className="text-indigo-900" />
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            System Administration
          </h1>
        </div>
        <p className="text-sm text-slate-500">
          Manage your profile and system settings.
        </p>
      </div>

      <ProfileTab />
    </div>
  );
}
